import { Show, createSignal } from 'solid-js';
import { A } from '@solidjs/router';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import Text from './Text';
import { RelativeTime } from './RelativeTime';
import { useToast } from './MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useActiveBot } from '../state/activeBot';
import { refreshBotLicenses, useBotLicense } from '../hooks/useBotLicense';
import { refreshLicenses } from '../hooks/useLicense';
import { BOT_LICENSE_NAME_KEYS } from '../services/grantsApi';
import styles from './BotLicenseBanner.module.css';

interface BotLicenseBannerProps {
    licenseType: string;
}

// Shown above a licensed feature's page while the selected bot has no license of that type, or while
// its license is running out on a grace period.
export default function BotLicenseBanner(props: BotLicenseBannerProps) {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId } = useActiveBot();
    const { slot, licensed, loaded } = useBotLicense(props.licenseType);
    const [assigning, setAssigning] = createSignal(false);

    const inGrace = () => slot()?.assignment?.state === 'Grace';
    const licenseName = () => t(BOT_LICENSE_NAME_KEYS[props.licenseType]);

    const assign = async () => {
        const id = botId();
        if (!id) return;
        setAssigning(true);
        try {
            const res = await api.botLicense.attach(id, props.licenseType);
            if (isOk(res.data)) {
                toast?.success(t('BOT_LICENSE_ASSIGNED', licenseName()));
                refreshBotLicenses();
                refreshLicenses();
            } else {
                toast?.error(firstError(res.data) ?? t('BOT_LICENSE_ASSIGN_FAILED'));
            }
        } finally {
            setAssigning(false);
        }
    };

    return (
        <Show when={botId() && loaded() && slot()}>
            <Show when={!licensed()} fallback={
                <Show when={inGrace()}>
                    <div class={`${styles.banner} ${styles.warning}`} role="status">
                        <i class={`fa-solid fa-hourglass-half ${styles.icon}`} />
                        <div class={styles.text}>
                            <span class={styles.title}>
                                {t('BOT_LICENSE_GRACE_TITLE', licenseName())}
                            </span>
                            <span class={styles.hint}>
                                {t('BOT_LICENSE_GRACE_HINT', slot()!.assignment!.holderName)}{' '}
                                <RelativeTime value={slot()!.assignment!.graceEndsAt} showAbsolute />
                            </span>
                        </div>
                    </div>
                </Show>
            }>
                <div class={`${styles.banner} ${styles.missing}`} role="status">
                    <i class={`fa-solid fa-key ${styles.icon}`} />
                    <div class={styles.text}>
                        <span class={styles.title}>
                            {t('BOT_LICENSE_MISSING_TITLE', licenseName())}
                        </span>
                        <span class={styles.hint}>
                            <Show when={slot()!.callerSeatsTotal > 0} fallback={<Text val="BOT_LICENSE_MISSING_NONE_HELD" />}>
                                <Show when={slot()!.callerSeatsFree > 0} fallback={<Text val="BOT_LICENSE_MISSING_ALL_USED" />}>
                                    {t('BOT_LICENSE_MISSING_FREE', String(slot()!.callerSeatsFree), String(slot()!.callerSeatsTotal))}
                                </Show>
                            </Show>
                        </span>
                    </div>
                    <div class={styles.actions}>
                        <Show when={slot()!.callerSeatsFree > 0} fallback={
                            <Show when={slot()!.callerSeatsTotal > 0}>
                                <A href="/mylicenses" class="btn btn-secondary btn-sm"><Text val="BOT_LICENSE_MANAGE" /></A>
                            </Show>
                        }>
                            <button class="btn btn-primary btn-sm" disabled={assigning()} onClick={() => void assign()}>
                                <Show when={assigning()} fallback={<><i class="fa-solid fa-key" /> <Text val="BOT_LICENSE_ASSIGN" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                            </button>
                        </Show>
                    </div>
                </div>
            </Show>
        </Show>
    );
}
